import { useEffect, useState, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { useRegionContext } from '../context/RegionContext';
import { useAuth } from '../context/AuthContext';
import { ExternallyControlledDialog } from './dialogs';
import { BASE_URI } from '../globals';

function LoginDialog({ onSuccess, open }) {
    const { login, isLoading, error, clearError } = useAuth();
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');

    const handleSubmit = async e => {
        e.preventDefault();
        const success = await login(username, password);
        if (success) {
            onSuccess();
        }
    };

    useEffect(() => {
        clearError();
    }, [username, password, clearError]);

    return (
        <ExternallyControlledDialog title="Inloggen vereist" open={open}>
            <Typography variant="body2" sx={{ mb: 2 }}>
                Voor deze regio is inloggen vereist.
            </Typography>
            <form onSubmit={handleSubmit}>
                <TextField
                    label="Gebruikersnaam"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                    autoComplete="username"
                />
                <TextField
                    label="Wachtwoord"
                    type="password"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    fullWidth
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                    autoComplete="current-password"
                />
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                <Button
                    type="submit"
                    sx={{ width: '100%' }}
                    variant="contained"
                    disabled={isLoading || !username || !password}>
                    {isLoading ? <CircularProgress size={24} /> : 'Inloggen'}
                </Button>
            </form>
        </ExternallyControlledDialog>
    );
}

function AccessDeniedDialog({ open }) {
    return (
        <ExternallyControlledDialog title="Geen toegang" open={open}>
            <Alert severity="error">
                Je hebt geen toegang tot deze regio. Neem contact op met de beheerder als je denkt
                dat dit een vergissing is.
            </Alert>
        </ExternallyControlledDialog>
    );
}

function RegionAccessCode({ children }) {
    const { region, regionData } = useRegionContext();
    const { isAuthenticated } = useAuth();
    const [accessStatus, setAccessStatus] = useState('loading'); // 'loading' | 'granted' | 'denied' | 'login_required'
    const [isChecking, setIsChecking] = useState(false);

    const checkAccess = useCallback(async () => {
        if (!regionData) {
            setAccessStatus('loading');
            return;
        }

        // If region is not secured, grant access immediately
        if (!regionData.secured) {
            setAccessStatus('granted');
            return;
        }

        // Get token directly from localStorage to ensure we have the latest value
        const currentToken = localStorage.getItem('auth_token');

        // Region is secured - need to check access
        if (!currentToken) {
            setAccessStatus('login_required');
            return;
        }

        // User is authenticated, check if they have access
        setIsChecking(true);
        try {
            const response = await fetch(`${BASE_URI}/region/${region}/access/`, {
                headers: {
                    Authorization: `Token ${currentToken}`
                }
            });

            if (response.ok) {
                setAccessStatus('granted');
            } else if (response.status === 403) {
                setAccessStatus('denied');
            } else {
                // Unexpected error, deny access to be safe
                setAccessStatus('denied');
            }
        } catch {
            setAccessStatus('denied');
        } finally {
            setIsChecking(false);
        }
    }, [regionData, region]);

    // Check access when region changes or on mount
    useEffect(() => {
        checkAccess();
    }, [checkAccess, isAuthenticated]);

    // Reset access status when region changes
    useEffect(() => {
        setAccessStatus('loading');
    }, [region]);

    const handleLoginSuccess = () => {
        // After login, re-check access
        checkAccess();
    };

    if (isChecking || accessStatus === 'loading') {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px'
                }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <LoginDialog onSuccess={handleLoginSuccess} open={accessStatus === 'login_required'} />
            <AccessDeniedDialog open={accessStatus === 'denied'} />
            {accessStatus === 'granted' && children}
        </>
    );
}

export default RegionAccessCode;
